import { toShortTimeString } from '@sola/utils/date.ts';

import type {
  FormError,
  GuestAgeRange,
  PreviousRegistrations,
  ShiftAssignments,
  VolunteerSlot,
} from '../../utils/volunteer-utils.ts';
import {
  getContactDisplayName,
  getRoleConfig,
  getSlotGroupKey,
  hasSlotTimeConflict,
  isSlotAtCapacity,
  meetsAgeRequirement,
} from '../../utils/volunteer-utils.ts';

// ── Shared types ─────────────────────────────────────────────────────────────

/** Border alert style for a slot card. `default` = no highlight. */
export type SlotCardAlertType = 'default' | 'negative' | 'brand';

export interface SlotCardContact {
  id: string;
  displayName: string;
  email: string | null;
  isAssigned: boolean;
  isDisabled: boolean;
  isPreviouslyRegistered?: boolean;
  disabledReason?:
    | 'conflict'
    | 'full'
    | 'ageRestriction'
    | 'previouslyRegistered'
    | 'sameDayConflict';
}

export interface SlotCardAssignedContact {
  name: string;
  isPreviouslyRegistered: boolean;
}

interface SlotCardInput {
  slot: VolunteerSlot;
  allSlots: VolunteerSlot[];
  assignments: ShiftAssignments;
  slotGroupIds: Map<string, string[]>;
  previousRegistrations: PreviousRegistrations;
  selectedContacts: Array<{
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    ageRange?: GuestAgeRange;
  }>;
  /** All known contacts (for resolving names of previously registered contacts not in current selection). */
  allContacts?: Array<{
    id: string;
    firstName: string | null;
    lastName: string | null;
  }>;
  /**
   * Per-contact group keys locked because that contact is booked elsewhere
   * that day (SAME_DAY_CONFLICT). Checked per contact so, in a mixed response,
   * the same-day contact shows the "booked that day" note while a duplicate
   * contact on the same shift still shows "already signed up for this shift".
   */
  sameDayRegistrations?: PreviousRegistrations;
}

export interface SlotCardDisplayData {
  timeDisplay: string;
  label: string;
  remaining: number;
  total: number;
  assignedContacts: Array<{ name: string; isPreviouslyRegistered: boolean }>;
  disabled: boolean;
  contacts: SlotCardContact[];
}

/** Derives all display state for a single SlotCard from raw registration data. */
export const getSlotCardDisplayData = ({
  slot,
  allSlots,
  assignments,
  slotGroupIds,
  previousRegistrations,
  selectedContacts,
  allContacts = [],
  sameDayRegistrations,
}: SlotCardInput): SlotCardDisplayData => {
  const groupKey = getSlotGroupKey(slot);
  const pool = slotGroupIds.get(groupKey) ?? [];

  // Count all new assignments against the pool (remainingCapacity from the API
  // already accounts for pre-existing registrations)
  const newAssignedCount = Object.values(assignments)
    .flat()
    .filter((id) => pool.includes(id)).length;
  const liveRemaining = slot.remainingCapacity - newAssignedCount;

  const assignedContacts = selectedContacts.filter((c) => {
    const cIds = assignments[c.id] ?? [];
    return cIds.some((id) => pool.includes(id));
  });

  const isFullyDisabled = liveRemaining <= 0 && assignedContacts.length === 0;

  const contacts: SlotCardContact[] = selectedContacts.map((c) => {
    const cIds = assignments[c.id] ?? [];
    const isAssigned = cIds.some((id) => pool.includes(id));

    // Check if this contact is previously registered for this specific slot (by name/start/end)
    const lockedGroupKeys = previousRegistrations[c.id] ?? [];
    const isPreviouslyRegistered = lockedGroupKeys.includes(groupKey);

    // Check age restriction for guest contacts (only guests have ageRange)
    const roleConfig = getRoleConfig(slot.description);
    const ageRestricted =
      !isAssigned &&
      !!c.ageRange &&
      !!roleConfig.rules.minAge &&
      !meetsAgeRequirement(c.ageRange, roleConfig.rules.minAge);

    const conflict =
      !isAssigned &&
      !ageRestricted &&
      hasSlotTimeConflict(c.id, slot, allSlots, assignments, slotGroupIds);
    const noCapacity =
      !isAssigned &&
      !ageRestricted &&
      !conflict &&
      isSlotAtCapacity(c.id, slot, pool, assignments);

    // Same-day is per contact: in a mixed response one contact may be locked
    // for a same-day conflict and another for a true duplicate on the same shift.
    const isSameDayLock = (sameDayRegistrations?.[c.id] ?? []).includes(
      groupKey,
    );

    let disabledReason: SlotCardContact['disabledReason'];
    if (isPreviouslyRegistered) {
      // A same-day lock reads differently from a true duplicate: the contact
      // isn't signed up for this shift, they're busy elsewhere that day.
      disabledReason = isSameDayLock
        ? 'sameDayConflict'
        : 'previouslyRegistered';
    } else if (ageRestricted) {
      disabledReason = 'ageRestriction';
    } else if (conflict) {
      disabledReason = 'conflict';
    } else if (noCapacity) {
      disabledReason = 'full';
    }

    return {
      id: c.id,
      displayName: getContactDisplayName(c, { variant: 'full' }),
      email: c.email,
      isAssigned,
      // Time conflicts are informational only — the contact can still select overlapping shifts.
      isDisabled: isPreviouslyRegistered || ageRestricted || noCapacity,
      isPreviouslyRegistered,
      disabledReason,
    };
  });

  const start = toShortTimeString(slot.startDate);
  const end = toShortTimeString(slot.endDate);

  // Include previously registered contacts not in current selection as non-removable indicators
  const assignedIds = new Set(assignedContacts.map((c) => c.id));
  const priorIndicators: Array<{
    name: string;
    isPreviouslyRegistered: boolean;
  }> = Object.entries(previousRegistrations)
    .filter(
      ([contactId, groupKeys]) =>
        !assignedIds.has(contactId) && groupKeys.includes(groupKey),
    )
    .flatMap(([contactId]) => {
      const contact =
        selectedContacts.find((c) => c.id === contactId) ??
        allContacts.find((c) => c.id === contactId);
      if (!contact) {
        return [];
      }
      return [
        {
          name: getContactDisplayName(contact, { variant: 'short' }),
          isPreviouslyRegistered: true,
        },
      ];
    });

  const assignedContactsList = [
    ...assignedContacts.map((c) => {
      const lockedGroupKeys = previousRegistrations[c.id] ?? [];
      const isPreviouslyRegistered = lockedGroupKeys.includes(groupKey);
      return {
        name: getContactDisplayName(c, { variant: 'short' }),
        isPreviouslyRegistered,
      };
    }),
    ...priorIndicators,
  ].sort(
    (a, b) =>
      Number(b.isPreviouslyRegistered) - Number(a.isPreviouslyRegistered),
  );

  return {
    timeDisplay: start && end ? `${start} – ${end}` : '',
    label: slot.description ?? '',
    remaining: liveRemaining,
    total: slot.remainingCapacity,
    assignedContacts: assignedContactsList,
    disabled: isFullyDisabled,
    contacts,
  };
};

export interface SlotCardAlertInput {
  groupKey: string;
  /** Active form-level errors; a `full-shift` entry arms the full-shift border. */
  formErrors: FormError[];
  /** Group keys highlighted because they filled up during submission. */
  fullShiftGroupKeys: ReadonlySet<string>;
  /** Group keys locked because a volunteer was already signed up (duplicate). */
  duplicateShiftGroupKeys: ReadonlySet<string>;
  /** Group keys locked because a volunteer is booked elsewhere that day. */
  sameDayShiftGroupKeys: ReadonlySet<string>;
}

/**
 * Resolve the alert border for a slot. A full shift takes precedence over a
 * duplicate or same-day lock.
 *
 * The full-shift highlight is transient and only shown while a `full-shift`
 * form error is active (it's cleared on a successful remap). Duplicate and
 * same-day locks persist until reset, so their border stays even after the
 * errors clear on navigation. Both use the same brand border.
 */
export const getSlotCardAlertType = ({
  groupKey,
  formErrors,
  fullShiftGroupKeys,
  duplicateShiftGroupKeys,
  sameDayShiftGroupKeys,
}: SlotCardAlertInput): SlotCardAlertType => {
  const hasFullShiftError = formErrors.some((e) => e.type === 'full-shift');
  if (hasFullShiftError && fullShiftGroupKeys.has(groupKey)) {
    return 'negative';
  }
  if (
    duplicateShiftGroupKeys.has(groupKey) ||
    sameDayShiftGroupKeys.has(groupKey)
  ) {
    return 'brand';
  }
  return 'default';
};

// ── Dialog capacity helpers ──────────────────────────────────────────────────

export interface DialogCapacityState {
  /** IDs of previously registered contacts (locked, non-removable). */
  lockedIds: Set<string>;
  /** Total slots available for new (non-locked) assignments. */
  totalAvailableForNew: number;
  /** Count of new (non-locked) contacts currently in the draft. */
  newSelectionCount: number;
  /** Whether no more new contacts can be added. */
  isAtCapacity: boolean;
}

/**
 * Computes capacity state for the slot assignment dialog.
 *
 * @param contacts - The contact list passed to the dialog.
 * @param remaining - Remaining capacity from the slot card (already subtracts current assignments).
 * @param draft - The current set of selected contact IDs in the dialog.
 */
export const getDialogCapacityState = (
  contacts: SlotCardContact[],
  remaining: number,
  draft: Set<string>,
): DialogCapacityState => {
  const lockedIds = new Set(
    contacts.filter((c) => c.isPreviouslyRegistered).map((c) => c.id),
  );

  const currentlyAssignedNewCount = contacts.filter(
    (c) => c.isAssigned && !c.isPreviouslyRegistered,
  ).length;

  const totalAvailableForNew = remaining + currentlyAssignedNewCount;

  const newSelectionCount = [...draft].filter(
    (id) => !lockedIds.has(id),
  ).length;

  const isAtCapacity = newSelectionCount >= totalAvailableForNew;

  return { lockedIds, totalAvailableForNew, newSelectionCount, isAtCapacity };
};

// ── Dialog contact state ─────────────────────────────────────────────────────

export interface DialogContactState {
  checked: boolean;
  isDisabled: boolean;
}

/**
 * Derives the interactive state for a single contact row inside the dialog.
 *
 * Capacity ("full") is governed dynamically by the dialog's draft state rather
 * than the static snapshot from the parent.
 */
export const getDialogContactState = (
  contact: SlotCardContact,
  draft: Set<string>,
  isAtCapacity: boolean,
): DialogContactState => {
  const checked = draft.has(contact.id);
  const capacityDisabled =
    !checked && !contact.isPreviouslyRegistered && isAtCapacity;
  const staticDisabled =
    contact.isDisabled && contact.disabledReason !== 'full';

  return { checked, isDisabled: staticDisabled || capacityDisabled };
};

/**
 * Determines whether a contact toggle should be allowed.
 * Returns `false` when the contact is hard-disabled (not just capacity-full).
 */
export const canToggleContact = (contact: SlotCardContact): boolean =>
  !(contact.isDisabled && contact.disabledReason !== 'full');

/**
 * Produces the next draft state after toggling a contact.
 * Enforces capacity limits — returns the previous draft unchanged if the
 * toggle would exceed available slots.
 */
export const toggleDraftContact = (
  id: string,
  prev: Set<string>,
  lockedIds: Set<string>,
  totalAvailableForNew: number,
): Set<string> => {
  const next = new Set(prev);

  if (next.has(id)) {
    next.delete(id);
    return next;
  }

  // Prevent adding beyond capacity (unless previously registered / locked)
  if (!lockedIds.has(id)) {
    const currentNewCount = [...prev].filter(
      (pid) => !lockedIds.has(pid),
    ).length;
    if (currentNewCount >= totalAvailableForNew) {
      return prev;
    }
  }

  next.add(id);
  return next;
};

/**
 * Filters the draft down to saveable contact IDs.
 * Excludes locked (previously registered) contacts and contacts that are
 * hard-disabled for non-capacity reasons.
 */
export const getSaveableContactIds = (
  draft: Set<string>,
  contacts: SlotCardContact[],
  lockedIds: Set<string>,
): string[] =>
  [...draft].filter((id) => {
    if (lockedIds.has(id)) {
      return false;
    }
    const contact = contacts.find((c) => c.id === id);
    return !(contact?.isDisabled && contact.disabledReason !== 'full');
  });
